import { useEffect, useState } from "react";
import styled from "styled-components";
import { fetchWrapper } from "../../utils/apiHandlers";
import LoadingComponent from "../LoadingComponent";
import theme from "../../theme";

interface Prop {
  userid: string;
}

const InsightsComp: React.FC<Prop> = ({ userid }) => {
  const [keywords, setkeywords] = useState<any[]>([])
  const [questions, setquestions] = useState<any[]>([])
  const [emails, setemails] = useState<any[]>([])
  const [loading, setloading] = useState(false)

  const load = async () => {
    if (!userid) return
    setloading(true)
    try {
      let [k, q, e]: any = await Promise.all([
        fetchWrapper.post("/api/analysis/getkeywords", { userid }),
        fetchWrapper.post("/api/analysis/getquestions", { userid }),
        fetchWrapper.post("/api/analysis/getemails", { userid }),
      ])
      setkeywords(k.keywords || [])
      setquestions(q.qa || [])
      setemails(e.emails || [])
    } catch (error) { }
    setloading(false)
  }

  useEffect(() => { load() }, [userid])

  let monthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
  let questionsThisMonth = questions.filter((q) => new Date(q.register_date).getTime() >= monthAgo).length
  let maxKeywordCount = Math.max(1, ...keywords.slice(0, 3).map((k) => k.count || 0))

  return (
    <MainContainer>
      <TopRow>
        <Title>Insights</Title>
        <RefreshLink onClick={load}>{loading ? "Loading…" : "Refresh"}</RefreshLink>
      </TopRow>
      {loading && <LoadingComponent />}

      <Stats>
        <StatTile>
          <StatLabel>QUESTIONS (30 DAYS)</StatLabel>
          <StatValue>{questionsThisMonth}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>TOTAL QUESTIONS LOGGED</StatLabel>
          <StatValue>{questions.length}</StatValue>
        </StatTile>
        <StatTile>
          <StatLabel>EMAILS CAPTURED</StatLabel>
          <StatValue style={{ color: theme.colors.success }}>{emails.length}</StatValue>
        </StatTile>
      </Stats>

      <Columns>
        <Col style={{ flex: 1 }}>
          <SectionTitle>Top keywords</SectionTitle>
          {keywords.length ? (
            <Card>
              {keywords.slice(0, 8).map((k) => (
                <BarRow key={k.keyword}>
                  <BarLabelRow>
                    <span>{k.keyword}</span>
                    <Muted>{k.count}</Muted>
                  </BarLabelRow>
                  <BarTrack><BarFill style={{ width: `${Math.max(6, (k.count / maxKeywordCount) * 100)}%` }} /></BarTrack>
                </BarRow>
              ))}
            </Card>
          ) : <EmptyCard>No keywords logged yet.</EmptyCard>}
        </Col>

        <Col style={{ flex: 1.4 }}>
          <SectionTitle>Recent questions</SectionTitle>
          {questions.length ? (
            <Card style={{ padding: 0 }}>
              {questions.slice(0, 10).map((q, i) => (
                <QRow key={i}>
                  <QText>&ldquo;{q.question}&rdquo;</QText>
                  <Muted>{new Date(q.register_date).toLocaleDateString()}</Muted>
                </QRow>
              ))}
            </Card>
          ) : <EmptyCard>No questions logged yet.</EmptyCard>}
        </Col>
      </Columns>

      <div>
        <SectionTitle>Captured leads</SectionTitle>
        {emails.length ? (
          <Card>
            {emails.slice(0, 10).map((e, i) => (
              <EmailRow key={i}>
                <b>{e.email}</b> — asked &ldquo;{(e.question || '').slice(0, 60)}{(e.question || '').length > 60 ? '…' : ''}&rdquo;
              </EmailRow>
            ))}
          </Card>
        ) : <EmptyCard>No emails captured yet.</EmptyCard>}
      </div>
    </MainContainer>
  );
};

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${theme.colors.text};
`

const RefreshLink = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${theme.colors.primary};
  cursor: pointer;
`

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
`

const StatTile = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 14px;
`

const StatLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${theme.colors.textMuted};
`

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  margin-top: 4px;
  color: ${theme.colors.text};
`

const Columns = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  @media (max-width: 900px) {
    flex-direction: column;
  }
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`

const SectionTitle = styled.div`
  font-weight: 800;
  font-size: 13px;
`

const Muted = styled.span`
  font-size: 11px;
  color: ${theme.colors.textMuted};
`

const EmptyCard = styled.div`
  background: ${theme.colors.background};
  border: 1px dashed ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 12px;
  font-size: 12px;
  color: ${theme.colors.textMuted};
`

const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radius.md};
  padding: 12px;
`

const BarRow = styled.div`
  margin-bottom: 10px;
  &:last-child { margin-bottom: 0; }
`

const BarLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  margin-bottom: 3px;
`

const BarTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${theme.colors.background};
`

const BarFill = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${theme.colors.primary};
`

const QRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 12px;
  font-size: 12px;
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
`

const QText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
`

const EmailRow = styled.div`
  font-size: 11px;
  padding: 6px 0;
  color: ${theme.colors.textMuted};
`

export default InsightsComp;
